import { 
    UsergroupAddOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined,
    SafetyCertificateOutlined,
    KeyOutlined,
    UnlockOutlined,
    LockOutlined
} from '@ant-design/icons';
import { StatisticsCard } from '../../../../../components/UI';
import { useStaffData } from '../hooks/useStaffData';

/**
 * Staff card component displaying staff, role, or permissions statistics based on active tab
 * Refactored to use shared data utilities and hooks
 */
const StaffCard = ({ activeTab = 0 }) => {
    const { 
        staffCountData,
        roleCountData,
        permissionsCountData, // Add this to your hook
        isLoadingEmployees,
        isLoadingRoles,
        isLoadingPermissions, // Add this to your hook
        isEmployeesError,
        isRolesError,
        isPermissionsError, // Add this to your hook
        employeesError,
        rolesError,
        permissionsError // Add this to your hook
    } = useStaffData();
    
    // Handle loading and error states
    const getLoadingState = () => {
        switch (activeTab) {
            case 0: return isLoadingEmployees;
            case 1: return isLoadingRoles;
            case 2: return isLoadingPermissions;
            default: return false;
        }
    };

    const getErrorState = () => {
        switch (activeTab) {
            case 0: return isEmployeesError;
            case 1: return isRolesError;
            case 2: return isPermissionsError;
            default: return false;
        }
    };

    const getError = () => {
        switch (activeTab) {
            case 0: return employeesError;
            case 1: return rolesError;
            case 2: return permissionsError;
            default: return null;
        }
    };

    const getErrorType = () => {
        switch (activeTab) {
            case 0: return 'staff';
            case 1: return 'roles';
            case 2: return 'permissions';
            default: return 'data';
        }
    };

    const isLoading = getLoadingState();
    const isError = getErrorState();
    const error = getError();
    const errorType = getErrorType();

    // Data configuration based on active tab
    const getCardData = () => {
        switch (activeTab) {
            case 0: // Staff tab
                return [
                    {
                        icon: UsergroupAddOutlined,
                        title: "All Staff",
                        count: staffCountData?.total ?? 0,
                        iconColor: "text-orange-600",
                        iconBgColor: "bg-orange-100"
                    },
                    {
                        icon: CheckCircleOutlined,
                        title: "Active Staff",
                        count: staffCountData?.totalActive ?? 0,
                        iconColor: "text-green-600",
                        iconBgColor: "bg-green-100"
                    },
                    {
                        icon: CloseCircleOutlined,
                        title: "Inactive Staff",
                        count: staffCountData?.totalInactive ?? 0,
                        iconColor: "text-red-600",
                        iconBgColor: "bg-red-100"
                    }
                ];

            case 1: // Roles tab
                return [
                    {
                        icon: SafetyCertificateOutlined,
                        title: "All Roles",
                        count: roleCountData?.total ?? 0,
                        iconColor: "text-blue-600",
                        iconBgColor: "bg-blue-100"
                    },
                    {
                        icon: CheckCircleOutlined,
                        title: "Active Roles",
                        count: roleCountData?.totalActive ?? 0,
                        iconColor: "text-green-600",
                        iconBgColor: "bg-green-100"
                    },
                    {
                        icon: CloseCircleOutlined,
                        title: "Inactive Roles",
                        count: roleCountData?.totalInactive ?? 0,
                        iconColor: "text-red-600",
                        iconBgColor: "bg-red-100"
                    }
                ];

            case 2: // Permissions tab
                return [
                    {
                        icon: KeyOutlined,
                        title: "All Permissions",
                        count: permissionsCountData?.total ?? 0,
                        iconColor: "text-purple-600",
                        iconBgColor: "bg-purple-100"
                    },
                  
                ];

            default:
                return [];
        }
    };

    const cardData = getCardData();

    // Use the global StatisticsCard component
    return (
        <StatisticsCard
            cardData={cardData}
            isLoading={isLoading}
            isError={isError}
            errorMessage={error?.message || 'Please check internet connection'}
            errorType={errorType}
            columns={3}
        />
    );
};

export default StaffCard;